/**
 * Invoice/Statement PDF shared styles.
 *
 * Lifted from ledger-flow-ifrs-beacon (Hindsight) `domains/invoicing/components/react-pdf/core/styles.ts`
 * with two adaptations for Afrilink:
 *   1. Default font is Helvetica (built-in, no licensing) instead of Calibri.
 *      Calibri can be re-enabled by registering the font and changing the
 *      fontFamily in the styles below — Hindsight required the brand match;
 *      Afrilink doesn't yet.
 *   2. Brand accent uses SightHost's brand orange (#F58C1E) instead of lime.
 *
 * Architecture: ARCHITECTURE_FRAMEWORK.md §3 — PDF assets live in @sighthost/ui
 * so both portal/ and (eventually) admin/ can render the same documents.
 */

import { StyleSheet, Font } from '@react-pdf/renderer';

// Disable hyphenation (avoids word breaks that look bad in PDFs).
Font.registerHyphenationCallback((word) => [word]);

/**
 * Brand palette mirroring `packages/ui/src/design-system/tokens/colors.ts`.
 * Kept inline here because @react-pdf/renderer uses a different style system
 * than the Tailwind classes the rest of the UI uses.
 */
export const colors = {
  primary: '#212121',      // brand ink
  secondary: '#6b7280',    // gray-500
  muted: '#9ca3af',        // gray-400
  background: '#ffffff',
  border: '#e5e7eb',       // gray-200
  headerBg: '#f3f4f6',     // gray-100
  accent: '#F58C1E',       // brand primary (SightHost orange)
  accentLight: '#fff7ed',  // orange-50
} as const;

export const documentStyles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    paddingTop: 30,
    paddingBottom: 50,
    paddingLeft: 30,
    paddingRight: 30,
    backgroundColor: colors.background,
  },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    alignItems: 'flex-start',
  },
  logo: {
    height: 60,
    width: 'auto',
    objectFit: 'contain',
  },
  documentTitle: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    textAlign: 'right',
  },
  documentNumber: {
    fontSize: 10,
    color: colors.secondary,
    textAlign: 'right',
    marginTop: 2,
  },

  addressGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  addressColumn: {
    width: '48%',
  },
  addressLabel: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: colors.secondary,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  companyName: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
  },
  addressLine: {
    fontSize: 9,
    color: colors.primary,
    marginTop: 1,
  },

  datesSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingTop: 8,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  dateBlock: {
    flexDirection: 'column',
  },
  dateLabel: {
    fontSize: 8,
    color: colors.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  dateValue: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginTop: 2,
  },

  table: {
    width: '100%',
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.headerBg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border,
    minHeight: 22,
    alignItems: 'center',
  },
  tableRowAlt: {
    backgroundColor: '#fafafa',
  },
  tableCell: {
    padding: 5,
    fontSize: 9,
  },
  tableCellHeader: {
    padding: 5,
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
  },
  tableCellLeft: { textAlign: 'left' },
  tableCellCenter: { textAlign: 'center' },
  tableCellRight: { textAlign: 'right' },

  totalsSection: {
    alignItems: 'flex-end',
    marginTop: 8,
    marginBottom: 16,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: 220,
    marginBottom: 3,
  },
  totalsLabel: {
    fontSize: 9,
    color: colors.secondary,
    width: 110,
    textAlign: 'right',
    paddingRight: 8,
  },
  totalsValue: {
    fontSize: 9,
    color: colors.primary,
    width: 110,
    textAlign: 'right',
  },
  totalsFinal: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },

  paymentSection: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 12,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: colors.primary,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  paymentDetail: {
    fontSize: 9,
    color: colors.secondary,
    marginBottom: 2,
  },

  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 8,
  },
  footerText: {
    fontSize: 7,
    color: colors.secondary,
    textAlign: 'center',
    marginBottom: 2,
  },
  footerCopyright: {
    fontSize: 7,
    color: colors.muted,
    textAlign: 'center',
  },
});

/**
 * Format a number as ZAR currency for PDF rendering.
 * Returns "R 1 234,56" — SA convention (space thousands, comma decimal).
 */
export function formatPDFCurrency(amount: number): string {
  const formatted = amount.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `R ${formatted}`;
}

/** Format an ISO date string as `dd MMM yyyy` for PDF rendering. */
export function formatPDFDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
